<script setup lang="ts">
    import { IInteraction } from '@/js/services/api/interaction/types';
    import { computed } from 'vue';
    import TranscriptionAudioPlayer from './TranscriptionAudioPlayer.vue';
    import { ChevronDownIcon } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, InteractionModulePermissionFlag } from '@/js/consts/permissions';
    import {
        estimateSegmentDurations,
        getTranscriptionDuration,
    } from '@/js/utils/transcriptionSegments';

    const { hasPermission } = usePermissions();

    const canStream = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.Streaming
    );

    const emit = defineEmits<{
        (e: 'current-time-update', currentTime: number): void;
    }>();

    const props = defineProps<{
        interaction: IInteraction;
    }>();

    const isVoiceInteraction = computed(() => {
        return props.interaction.channelType === 0;
    });

    const duration = computed(() =>
        getTranscriptionDuration(
            props.interaction.transcription,
            isVoiceInteraction.value,
            props.interaction.summary?.audioFileLength || 0
        )
    );

    const parsedSegments = computed(() => {
        return estimateSegmentDurations(
            props.interaction.transcription,
            duration.value,
            isVoiceInteraction.value
        );
    });

    const customerSegments = computed(() => {
        return parsedSegments.value.filter((segment) => segment.speaker === 'Customer');
    });

    const agentSegments = computed(() => {
        return parsedSegments.value.filter((segment) => segment.speaker === 'Agent');
    });
</script>

<template>
    <details v-if="isVoiceInteraction && canStream" class="Accordion group audio-strip" open>
        <summary class="as-summary">
            <span class="text-overline-sm text-gray-medium">
                {{ __('interaction-audio-player-label') }}
            </span>
            <span class="as-toggle">
                <span class="hidden group-open:inline">
                    {{ __('interaction-audio-player-hide') }}
                </span>
                <span class="inline group-open:hidden">
                    {{ __('interaction-audio-player-show') }}
                </span>
                <ChevronDownIcon class="as-chevron" />
            </span>
        </summary>

        <div class="mt-3.5">
            <TranscriptionAudioPlayer
                :interaction="interaction"
                :customer-segments="customerSegments"
                :agent-segments="agentSegments"
                @current-time-update="emit('current-time-update', $event)"
            />
        </div>
    </details>
</template>
